import {
  MAXIMUM_CHARACTERS_SEARCH,
  MINIMUM_CHARACTERS_SEARCH,
  PageLoader,
  useAppContext,
  useConfig,
} from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import {
  Column,
  Columns,
  ContentBlock,
  Heading,
  IconSearch,
  Stack,
  TextField,
} from 'braid-design-system';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useService } from '../../../shared/providers/serviceProvider';
import type { SearchCompanyProfileResult } from '../../../shared/services/companyProfileService/types';
import { trackViewEvents } from '../../../shared/tracking/candidateApp/trackViewEvents';

import { NoResults } from '../../components/noResults/NoResults';
import { MainLayout } from '../../layouts/mainLayout/MainLayout';

import { useMelwaysLink } from '@seek/melways-react';

import { useDebounce } from 'use-debounce';
import { ROOT_PATH } from '../../../paths';
import { CompanySearchCard } from '../../components/companySearchCard/CompanySearchCard';
import translations from './.vocab';

export const SearchPage = () => {
  const { t } = useTranslations(translations);
  const { companyProfileService } = useService();
  const config = useConfig();
  const context = useAppContext();

  const [searchParams] = useSearchParams();
  const [searchCompaniesResult, setSearchCompaniesResult] =
    useState<SearchCompanyProfileResult>([]);
  const [searchIsLoading, setSearchIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get('term')?.toString() || '',
  );
  const [debouncedSearchTerm] = useDebounce(searchTerm, 400);

  const navigate = useNavigate();
  const { language } = useConfig();
  const localize = useMelwaysLink();

  const searchCompanyProfile = async (companyName: string) => {
    setSearchCompaniesResult([]);

    if (
      companyName &&
      companyName.length >= MINIMUM_CHARACTERS_SEARCH &&
      companyName.length <= MAXIMUM_CHARACTERS_SEARCH
    ) {
      setSearchIsLoading(true);
      const result = await companyProfileService.getCompanyProfilesByName({
        name: companyName,
        zone: config.zone,
        published: true,
        limit: 50,
      });

      setSearchCompaniesResult(result);
      window.scroll({ top: 0, behavior: 'smooth' });
      setSearchIsLoading(false);
      trackViewEvents.searchResultsPage({
        config,
        context,
        searchKeyword: companyName,
        companyCount: result.length,
      });
    } else {
      setSearchIsLoading(false);
    }
  };

  useEffect(() => {
    void searchCompanyProfile(debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  return (
    <MainLayout>
      <ContentBlock width="small">
        <Stack space="xlarge">
          <Heading level="3">
            {t("Search results for 'searchTerm'", { searchTerm })}
          </Heading>
          <Columns space="small" collapseBelow="tablet">
            <Column>
              <TextField
                id="company-search-bar"
                placeholder={t('Search by company name')}
                icon={<IconSearch />}
                aria-label={t('Search by company name')}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.currentTarget.value);
                }}
              />
            </Column>
          </Columns>

          {searchIsLoading ? (
            <PageLoader />
          ) : (
            <Stack space="small">
              {searchCompaniesResult.length > 0 ? (
                searchCompaniesResult.map((company) => (
                  <CompanySearchCard
                    key={company.slug}
                    name={company.name}
                    logo={company.logoUrl}
                    averageCompanyRating={company.reviewRating}
                    reviewsCount={company.reviewCount}
                    onClick={() => {
                      window.scroll(0, 0);
                      void navigate(
                        `${localize({
                          path: `/${ROOT_PATH}/${company.slug}`,
                          language,
                        })}`,
                      );
                    }}
                  />
                ))
              ) : (
                <NoResults />
              )}
            </Stack>
          )}
        </Stack>
      </ContentBlock>
    </MainLayout>
  );
};
